import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { CommonService } from '../common/common.service';
import { ConstantService } from '../constant/constant.service';

// Phone push: register the device token with the API after login, remove it on logout,
// and hand a tapped notification's deeplink to the same handler emails/SMS links use.
@Injectable({
  providedIn: 'root'
})
export class PushService {
  private token: string = null;
  private onTap: (url: string) => void = null;
  private listening = false;

  constructor(
    private common: CommonService,
    private constant: ConstantService,
    private zone: NgZone,
  ) { }

  // At app start. onTap gets a tapped notification's deeplink URL.
  init(onTap: (url: string) => void) {
    this.onTap = onTap;
    this.listen();
  }

  // Listeners must exist before register() fires 'registration', whichever of init/register runs first.
  private listen() {
    if (this.listening || !Capacitor.isNativePlatform()) return;
    this.listening = true;

    PushNotifications.addListener('registration', async ({ value }) => {
      this.token = value;
      try {
        await this.common.callApi(
          this.constant.DEVICE_V2,
          { token: value, platform: Capacitor.getPlatform() },
          'POST', '', false, false, true
        ).toPromise();
      } catch (e) {
        console.error('push: device register failed', e);
      }
    });

    PushNotifications.addListener('registrationError', (e) => console.error('push: registration error', e));

    PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
      const url = notification?.data?.deeplink;
      if (url && this.onTap) this.zone.run(() => this.onTap(url));
    });
  }

  // After login / on boot with a session. Asks permission the first time only.
  async register() {
    if (!Capacitor.isNativePlatform()) return;
    this.listen();
    try {
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive === 'prompt') perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') return;
      await PushNotifications.register(); // fires 'registration' with the token
    } catch (e) {
      console.error('push: register failed', e);
    }
  }

  // Before logout clears the session. Never blocks the logout.
  async unregister() {
    if (!this.token) return;
    const token = this.token;
    this.token = null;
    try {
      await this.common.callApi(this.constant.DEVICE_V2, { token }, 'DELETE', '', false, false, true).toPromise();
    } catch (e) {
      console.error('push: device remove failed', e);
    }
  }
}
